export type SegmentKind = 'rollers' | 'crest' | 'kicker' | 'climb' | 'drop' | 'washboard' | 'flat';

export interface EnvironmentDef {
  id: string;
  name: string;
  /** Fixed route seed: the same every run, so the route is learnable. */
  seed: number;
  gravity: number;
  groundFriction: number;
  /** Relative pick weights once the archetype is unlocked. */
  weights: Record<Exclude<SegmentKind, 'flat'>, number>;
  /** Distance (m) at which each archetype enters the procedural mix. */
  unlockAt: Record<Exclude<SegmentKind, 'flat'>, number>;
  /** Max sustained climb slope (rise/run) at full difficulty; tan(32°) ≈ 0.62. */
  maxSlope: number;
  /** Gas-can spacing at the start and at full difficulty. */
  fuelSpacing: { start: number; end: number };
  /** Distance at which difficulty reaches 1. */
  fullDifficultyAt: number;
  /** Shown on the map screen. */
  blurb: string;
  /** Synthesized ambience bed (src/audio/sound.ts). */
  ambience: 'terraces' | 'highlands' | 'village' | 'beach' | 'volcano';
  /** Unlocked by reaching `distance` metres in another environment; null = open from the start. */
  unlock: { after: string; distance: number } | null;
  visual: EnvironmentVisual;
  /** Roadside scatter (Kiln models, instanced): a pure function of the seed and chunk index. */
  props: PropRule[];
  /** Fence-like rows that follow the ground just behind the drive line (fences, tea hedges). */
  rows: RowRule | null;
  /** Buildings on the flattest spot near every `every` metres (k ≥ 1), offset by `offset`. */
  landmarks: LandmarkRule[];
  palette: {
    skyTop: string;
    skyHorizon: string;
    sun: string;
    ground: string;
    groundLip: string;
    groundDeep: string;
    farLayer: string;
    midLayer: string;
  };
}

export type PropModel =
  | 'palm' | 'banana' | 'fence' | 'warung' | 'school' | 'kopdes' | 'dapur' | 'house'
  | 'teaBush' | 'pine' | 'rock' | 'shrub' | 'jukung' | 'umbrella' | 'gate' | 'lamp' | 'gerobak';

export interface PropRule {
  model: PropModel;
  /** Instances per 50 m chunk: min + floor(rand × (max - min + 1)). */
  perChunk: [number, number];
  /** Depth behind the drive line (negative Z), near..far. */
  z: [number, number];
  scale: [number, number];
  /** Random yaw (trees, rocks); false keeps the model facing the camera (buildings, boats). */
  randomYaw: boolean;
}

export interface RowRule {
  model: PropModel;
  /** Chance a chunk gets a row. */
  chance: number;
  /** Spacing along X between row pieces. */
  pitch: number;
  z: number;
}

export interface LandmarkRule {
  model: PropModel;
  every: number;
  offset: number;
  z: number;
  /** Half width kept clear of other props. */
  halfWidth: number;
  /** Optional text painted on the building's signboard. */
  sign?: { text: string; y: number; z: number; width: number; height: number; color: string; ink: string };
}

/** The start-line building every delivery leaves from: the Dapur MBG kitchen (SPPG), in every environment. */
export const KITCHEN: LandmarkRule = {
  model: 'dapur', every: 0, offset: 1, z: -6.6, halfWidth: 6,
  sign: { text: 'DAPUR MBG\nSATUAN PELAYANAN PEMENUHAN GIZI', y: 2.73, z: 2.74, width: 3.9, height: 0.66, color: '#ffffff', ink: '#1f5fa8' },
};

export interface BackdropLayer {
  file: string;
  /** Distance behind the play plane (negative Z). */
  z: number;
  /** World height of the plane. */
  height: number;
  /** World height of one image tile (the image keeps its aspect ratio). */
  tileHeight: number;
  /** Fraction of camera motion the image scrolls by. */
  parallax: number;
  /** How much the layer follows the camera's height (1 = locked to the camera). */
  followY: number;
  offsetY: number;
  /** Which band of the image (0 = bottom) starts at the bottom of the plane. */
  vOffset: number;
  /** Horizontal texture shift at x = 0, to frame the hero feature at the start. */
  uOffset: number;
  tint: number;
  transparent: boolean;
}

export interface EnvironmentVisual {
  /** Ground textures (public/assets/images/…): verges and lip, the track, the bank face. */
  textures: { verge: string; track: string; bank: string };
  backdrop: BackdropLayer[];
  fog: [near: number, far: number];
  sunIntensity: number;
  exposure: number;
}

/** Schools are the delivery destinations: one at every delivery mark (see DELIVERY in tuning.ts). */
const SCHOOL: LandmarkRule = {
  model: 'school', every: 500, offset: 0, z: -5.2, halfWidth: 6.4,
  sign: { text: 'SD NEGERI', y: 3.62, z: 2.67, width: 2.5, height: 0.52, color: '#fbf8f0', ink: '#1d4f8a' },
};
const KOPDES: LandmarkRule = {
  model: 'kopdes', every: 1000, offset: 750, z: -4.7, halfWidth: 4.8,
  // Two lines, red on white, like the real Koperasi Merah Putih signs (art/reference).
  sign: { text: 'KOPERASI\nMERAH PUTIH', y: 4.28, z: 1.86, width: 4.5, height: 1.1, color: '#fbf8f0', ink: '#c62828' },
};
const WARUNG: LandmarkRule = { model: 'warung', every: 1000, offset: 250, z: -3.6, halfWidth: 1.7 };

export const BUKIT_SAWAH: EnvironmentDef = {
  id: 'bukit-sawah',
  name: 'Bukit Sawah',
  /** Same fixed route as the Phase 1 slice. */
  seed: 0x5a17f1a7,
  gravity: 9.81,
  groundFriction: 1.0,
  weights: { rollers: 3, crest: 3, kicker: 2, climb: 2, drop: 2, washboard: 1 },
  unlockAt: { rollers: 0, crest: 0, kicker: 150, climb: 300, drop: 400, washboard: 700 },
  maxSlope: 0.62,
  fuelSpacing: { start: 300, end: 520 },
  fullDifficultyAt: 2400,
  blurb: 'Rice terraces under the volcano. Where every delivery starts.',
  ambience: 'terraces',
  unlock: null,
  visual: {
    textures: { verge: 'tex-grass.jpg', track: 'tex-dirt.jpg', bank: 'tex-soil.jpg' },
    backdrop: [
      // One far tile (~510 m) is wider than the widest view, so only one volcano is ever on screen.
      { file: 'bg-far.jpg', z: -300, height: 230, tileHeight: 620, parallax: 0.04, followY: 0.92, offsetY: 30, vOffset: 0.37, uOffset: -0.5, tint: 0xffffff, transparent: false },
      // Two joined village strips (~120 m per tile): the mosque appears about once per screen width.
      { file: 'bg-mid.webp', z: -110, height: 48, tileHeight: 48, parallax: 0.22, followY: 0.55, offsetY: -17, vOffset: 0, uOffset: 0, tint: 0xdfe9ee, transparent: true },
    ],
    fog: [70, 340],
    sunIntensity: 2.4,
    exposure: 1.05,
  },
  props: [
    { model: 'palm', perChunk: [2, 3], z: [3, 6.5], scale: [0.8, 1.2], randomYaw: true },
    { model: 'banana', perChunk: [1, 3], z: [2.4, 4.9], scale: [0.85, 1.2], randomYaw: true },
    { model: 'house', perChunk: [0, 1], z: [8, 10], scale: [0.95, 1.05], randomYaw: false },
  ],
  rows: { model: 'fence', chance: 0.45, pitch: 3, z: -1.9 },
  landmarks: [SCHOOL, KOPDES, WARUNG],
  // Rice-terrace hills under a volcano: green terrace tops over dark soil, blue tropical sky.
  palette: {
    skyTop: '#3d8fd1',
    skyHorizon: '#d6ecf3',
    sun: '#fff4d6',
    ground: '#6f9e3a',
    groundLip: '#c9e27a',
    groundDeep: '#5b3d26',
    farLayer: '#8798ab',
    midLayer: '#4d8547',
  },
};



/** Backdrop and ground art for an environment that lives in public/assets/images/<id>/. */
function envVisual(id: string, opts: { midTint: number; fog: [number, number]; sun: number; exposure: number }): EnvironmentVisual {
  return {
    textures: { verge: `${id}/tex-verge.jpg`, track: `${id}/tex-track.jpg`, bank: `${id}/tex-bank.jpg` },
    backdrop: [
      { file: `${id}/bg-far.jpg`, z: -300, height: 230, tileHeight: 620, parallax: 0.04, followY: 0.92, offsetY: 30, vOffset: 0.37, uOffset: -0.5, tint: 0xffffff, transparent: false },
      { file: `${id}/bg-mid.webp`, z: -110, height: 48, tileHeight: 48, parallax: 0.22, followY: 0.55, offsetY: -17, vOffset: 0, uOffset: 0, tint: opts.midTint, transparent: true },
    ],
    fog: opts.fog,
    sunIntensity: opts.sun,
    exposure: opts.exposure,
  };
}

export const KEBUN_TEH: EnvironmentDef = {
  id: 'kebun-teh',
  name: 'Kebun Teh',
  blurb: 'Misty tea-plantation highlands. Tight rollers and steep, damp climbs.',
  ambience: 'highlands',
  unlock: { after: 'bukit-sawah', distance: 800 },
  seed: 0x7ea0c0de,
  gravity: 9.81,
  groundFriction: 0.9,
  weights: { rollers: 4, crest: 2, kicker: 1, climb: 3, drop: 2, washboard: 2 },
  unlockAt: { rollers: 0, crest: 0, kicker: 200, climb: 220, drop: 400, washboard: 500 },
  maxSlope: 0.66,
  fuelSpacing: { start: 280, end: 480 },
  fullDifficultyAt: 2200,
  visual: envVisual('kebun-teh', { midTint: 0xe4ece8, fog: [45, 260], sun: 2.0, exposure: 1.0 }),
  props: [
    { model: 'pine', perChunk: [2, 4], z: [4, 8], scale: [0.8, 1.25], randomYaw: true },
    { model: 'teaBush', perChunk: [4, 7], z: [2.6, 6], scale: [0.9, 1.1], randomYaw: false },
    { model: 'house', perChunk: [0, 1], z: [9, 11], scale: [0.9, 1], randomYaw: false },
  ],
  rows: { model: 'teaBush', chance: 0.7, pitch: 2.4, z: -2.1 },
  landmarks: [SCHOOL, KOPDES, WARUNG],
  palette: {
    skyTop: '#7fa9c9', skyHorizon: '#e3ece9', sun: '#fff7e8', ground: '#4f8a3a', groundLip: '#a9d36b',
    groundDeep: '#4a3626', farLayer: '#93a6a8', midLayer: '#4a7d4a',
  },
};

export const JALAN_KAMPUNG: EnvironmentDef = {
  id: 'jalan-kampung',
  name: 'Jalan Kampung',
  blurb: 'Village lanes: concrete ramps, speed bumps and warung on every corner.',
  ambience: 'village',
  unlock: { after: 'kebun-teh', distance: 800 },
  seed: 0x4a1a4b09,
  gravity: 9.81,
  groundFriction: 1.1,
  weights: { rollers: 2, crest: 2, kicker: 4, climb: 1, drop: 1, washboard: 3 },
  unlockAt: { rollers: 0, crest: 0, kicker: 100, climb: 350, drop: 450, washboard: 250 },
  maxSlope: 0.55,
  fuelSpacing: { start: 300, end: 500 },
  fullDifficultyAt: 2400,
  // The midground joins two village strips (one with the mosque): ~140 m per tile, one mosque per screen.
  visual: envVisual('jalan-kampung', { midTint: 0xeee7e0, fog: [70, 340], sun: 2.5, exposure: 1.05 }),
  props: [
    { model: 'house', perChunk: [2, 3], z: [6, 9], scale: [0.9, 1.1], randomYaw: false },
    { model: 'lamp', perChunk: [2, 3], z: [1.9, 2.2], scale: [1, 1], randomYaw: false },
    { model: 'gerobak', perChunk: [0, 1], z: [2.8, 3.4], scale: [1, 1], randomYaw: false },
    { model: 'banana', perChunk: [1, 2], z: [3.5, 5], scale: [0.8, 1.1], randomYaw: true },
  ],
  rows: { model: 'fence', chance: 0.3, pitch: 3, z: -1.9 },
  landmarks: [SCHOOL, { ...KOPDES, every: 500, offset: 250 }],
  palette: {
    skyTop: '#4a9ad6', skyHorizon: '#e6eef0', sun: '#fff1d6', ground: '#7a9c48', groundLip: '#cfe08a',
    groundDeep: '#5a4634', farLayer: '#8f9aa6', midLayer: '#6b7f62',
  },
};

export const PANTAI_BALI: EnvironmentDef = {
  id: 'pantai-bali',
  name: 'Pantai Bali',
  blurb: 'Soft sand, cliff-top kickers and long drops toward the surf.',
  ambience: 'beach',
  unlock: { after: 'jalan-kampung', distance: 900 },
  seed: 0xba11b3ac,
  gravity: 9.81,
  groundFriction: 0.78,
  weights: { rollers: 3, crest: 2, kicker: 3, climb: 1, drop: 3, washboard: 1 },
  unlockAt: { rollers: 0, crest: 0, kicker: 150, climb: 400, drop: 250, washboard: 800 },
  maxSlope: 0.58,
  fuelSpacing: { start: 300, end: 520 },
  fullDifficultyAt: 2400,
  visual: envVisual('pantai-bali', { midTint: 0xf2ece2, fog: [80, 360], sun: 2.6, exposure: 1.08 }),
  props: [
    { model: 'palm', perChunk: [2, 4], z: [3, 7], scale: [0.9, 1.3], randomYaw: true },
    { model: 'umbrella', perChunk: [0, 2], z: [3, 5], scale: [1, 1], randomYaw: true },
    { model: 'jukung', perChunk: [0, 1], z: [6, 9], scale: [1, 1], randomYaw: false },
    { model: 'gate', perChunk: [0, 1], z: [9, 11], scale: [0.9, 1], randomYaw: false },
  ],
  rows: null,
  landmarks: [SCHOOL, WARUNG],
  palette: {
    skyTop: '#2f9fd8', skyHorizon: '#e8f4f2', sun: '#fff6dc', ground: '#d9c08a', groundLip: '#f2e2b0',
    groundDeep: '#8a6b45', farLayer: '#7fb6c8', midLayer: '#5d9a6a',
  },
};

export const KAWAH_BROMO: EnvironmentDef = {
  id: 'kawah-bromo',
  name: 'Kawah Bromo',
  blurb: 'The volcanic ash sea at dawn. Loose sand, tall dunes, big drops.',
  ambience: 'volcano',
  unlock: { after: 'pantai-bali', distance: 1000 },
  seed: 0xb40a0123,
  gravity: 9.81,
  groundFriction: 0.72,
  weights: { rollers: 2, crest: 3, kicker: 2, climb: 3, drop: 3, washboard: 1 },
  unlockAt: { rollers: 0, crest: 0, kicker: 150, climb: 250, drop: 300, washboard: 900 },
  maxSlope: 0.6,
  fuelSpacing: { start: 320, end: 580 },
  fullDifficultyAt: 2000,
  visual: envVisual('kawah-bromo', { midTint: 0xeae2da, fog: [60, 320], sun: 2.2, exposure: 1.0 }),
  props: [
    { model: 'rock', perChunk: [2, 4], z: [2.6, 8], scale: [0.7, 1.4], randomYaw: true },
    { model: 'shrub', perChunk: [3, 6], z: [2.2, 6], scale: [0.8, 1.2], randomYaw: true },
    { model: 'pine', perChunk: [0, 1], z: [8, 11], scale: [0.8, 1.1], randomYaw: true },
  ],
  rows: null,
  landmarks: [SCHOOL, WARUNG],
  palette: {
    skyTop: '#e99a7a', skyHorizon: '#f7dcc4', sun: '#ffd9a8', ground: '#8a8378', groundLip: '#b9b0a2',
    groundDeep: '#3e3832', farLayer: '#9a8a8a', midLayer: '#6e655d',
  },
};

/** Map order: each one unlocks from the previous. */
export const ENVIRONMENTS: readonly EnvironmentDef[] = [BUKIT_SAWAH, KEBUN_TEH, JALAN_KAMPUNG, PANTAI_BALI, KAWAH_BROMO];

export function environmentById(id: string): EnvironmentDef | undefined {
  return ENVIRONMENTS.find((e) => e.id === id);
}
